<template>
  <section class="pct" aria-labelledby="pct-h">
    <div class="pct-titles">
      <div class="pct-eyebrow">Consideration</div>
      <h2 id="pct-h">How {{ providerName }} compares</h2>
      <p>The factors AI weighed when choosing providers for these questions, and how much each one mattered.</p>
    </div>

    <v-skeleton-loader v-if="pending && !comparison" type="table-heading, table-row-divider@3" />

    <template v-else-if="comparison && comparison.products && comparison.products.length">
      <p v-if="comparison.fallback" class="pct-fallback">
        {{ providerName }} wasn't in AI's top 5, so we're comparing them with the providers AI named most.
      </p>
      <div class="pct-scroll">
        <div role="table" :aria-label="`How ${providerName} compares`" class="pct-table">
          <div role="row" class="pct-row pct-head" :style="gridStyle">
            <span role="columnheader" class="th">Provider</span>
            <span role="columnheader" class="th">Overall fit</span>
            <span v-for="f in comparison.factors" :key="f.key" role="columnheader" class="col">
              <span class="th">{{ f.name }}</span>
              <span class="wrow">
                <span class="bar" aria-hidden="true"><span class="fill" :style="{ width: (f.weight / maxWeight * 100) + '%' }" /></span>
                <span class="w">{{ f.weight }}%</span>
              </span>
            </span>
          </div>
          <div
            v-for="(p, pi) in sortedProducts"
            :key="pi"
            role="row"
            class="pct-row"
            :class="{ me: p.is_primary }"
            :style="gridStyle"
          >
            <span role="cell" class="who">
              <strong>{{ p.name }}</strong>
              <span class="h">{{ p.hospital }}</span>
              <span class="seen">In {{ p.seen_in }} of {{ questionCount }} provider queries</span>
            </span>
            <span role="cell" class="fit">{{ fmt(p.overall_fit) }}<span class="of">/10</span></span>
            <span v-for="f in comparison.factors" :key="f.key" role="cell" class="cell">
              <template v-for="cell in [cellFor(p, f.key)]" :key="f.key">
                <span class="ctop">
                  <span>{{ cell.text || '\u2014' }}</span>
                  <button
                    v-if="cell.urls.length"
                    type="button"
                    class="lnk"
                    :class="{ on: isOpen(pi, f.key) }"
                    :aria-expanded="isOpen(pi, f.key) ? 'true' : 'false'"
                    aria-label="Show sources"
                    @click="toggle(pi, f.key)"
                  >
                    <v-icon size="16">mdi-link-variant</v-icon>
                  </button>
                </span>
                <span v-if="cell.text && !cell.urls.length" class="nosrc">No source — from AI's memory</span>
                <span v-if="cell.urls.length && isOpen(pi, f.key)" class="pop">
                  <span v-if="cell.quote" class="quote">&ldquo;{{ cell.quote }}&rdquo;</span>
                  <ul>
                    <li v-for="(u, ui) in cell.urls" :key="ui">
                      <a v-if="safeUrl(u.url)" :href="u.url" target="_blank" rel="noopener noreferrer">{{ siteOf(u) }}</a>
                      <span v-else class="plain">{{ siteOf(u) }}</span>
                      <span class="t">{{ u.title }}</span>
                      <span v-if="u.validated" title="Link checked" class="ok">&#10003;</span>
                    </li>
                  </ul>
                </span>
              </template>
            </span>
          </div>
        </div>
      </div>
    </template>

    <p v-else class="pct-fallback">No comparison yet for this AI tool.</p>

    <p class="pct-foot">
      AI picks these factors for each specialty, so they can change between audits. Condition expertise and Credentials are always included, so you can track them over time. Click the link icon to see where AI found each claim.
    </p>
  </section>
</template>

<script>
export default {
  name: 'ProviderComparisonTable',
  props: {
    comparison: { type: Object, default: null },
    providerName: { type: String, default: '' },
    questionCount: { type: Number, default: 9 },
    pending: { type: Boolean, default: false }
  },
  data() {
    return { openCells: {} }
  },
  computed: {
    maxWeight() {
      return Math.max(30, ...((this.comparison?.factors || []).map(f => f.weight)))
    },
    // By Overall fit, best first. A missing or non-numeric fit goes last; ties keep the
    // server order. The selected provider is sorted with the rest (its row stays highlighted).
    sortedProducts() {
      const fit = p => (p.overall_fit == null || p.overall_fit === '' || !Number.isFinite(Number(p.overall_fit)) ? null : Number(p.overall_fit))
      return (this.comparison?.products || [])
        .map((p, i) => ({ p, i, f: fit(p) }))
        .sort((a, b) => {
          if (a.f === null && b.f === null) return a.i - b.i
          if (a.f === null) return 1
          if (b.f === null) return -1
          return b.f - a.f || a.i - b.i
        })
        .map(x => x.p)
    },
    gridStyle() {
      const n = this.comparison?.factors?.length || 5
      return { gridTemplateColumns: `minmax(220px, 1.2fr) 90px repeat(${n}, minmax(0, 1fr))` }
    }
  },
  methods: {
    fmt(n) {
      return n == null ? '—' : Number(n).toFixed(1).replace(/\.0$/, '')
    },
    // reasoning[key] is [{text, citation_urls}] but may arrive as a plain string.
    cellFor(p, key) {
      const raw = p.reasoning?.[key]
      const quote = p.quotes?.[key] || ''
      if (Array.isArray(raw)) {
        const items = raw.filter(Boolean)
        return {
          text: items.map(r => (typeof r === 'string' ? r : r.text)).filter(Boolean).join(' '),
          urls: items.flatMap(r => (typeof r === 'string' ? [] : r.citation_urls || [])),
          quote
        }
      }
      return { text: typeof raw === 'string' ? raw : '', urls: [], quote }
    },
    safeUrl(u) {
      return /^https?:\/\//i.test(u || '')
    },
    siteOf(u) {
      try { return new URL(u.url).hostname.replace(/^www\./, '') || u.title || u.url } catch (e) { return u.title || u.url }
    },
    isOpen(pi, key) {
      return !!this.openCells[`${pi}-${key}`]
    },
    toggle(pi, key) {
      const k = `${pi}-${key}`
      this.openCells = { ...this.openCells, [k]: !this.openCells[k] }
    }
  }
}
</script>

<style scoped>
.pct { background: #fff; border: 1px solid #e6e1d4; border-radius: 12px; padding: 28px; display: flex; flex-direction: column; gap: 20px; }
.pct-titles { display: flex; flex-direction: column; gap: 6px; max-width: 760px; }
.pct-eyebrow { font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #0E7C80; }
.pct h2 { margin: 0; font-size: 24px; font-weight: 700; }
.pct p { margin: 0; color: #4a5568; }
.pct-fallback { background: var(--brand-amber-100); color: #6b4f00 !important; border-radius: 8px; padding: 10px 14px; font-size: 14px; }
.pct-foot { font-size: 13px; }
.pct-scroll { overflow-x: auto; border: 1px solid #e6e1d4; border-radius: 10px; }
.pct-table { min-width: 1180px; }
.pct-row { display: grid; gap: 14px; padding: 16px; border-top: 1px solid #ece7da; font-size: 14px; align-items: start; }
.pct-row.me { background: #f1f7fd; box-shadow: inset 4px 0 0 var(--brand-ocean-600); }
.pct-head { border-top: none; background: var(--brand-sand-100); padding: 12px 16px; align-items: end; }
.th { font-size: 12px; font-weight: 700; color: #4a5568; text-transform: uppercase; letter-spacing: 0.04em; }
.col { display: flex; flex-direction: column; gap: 6px; }
.wrow { display: flex; align-items: center; gap: 6px; }
.bar { flex: 1 1 auto; height: 6px; background: #e6e1d4; border-radius: 999px; overflow: hidden; display: block; }
.fill { display: block; height: 100%; background: var(--brand-ocean-600); border-radius: 999px; }
.w { font-size: 13px; font-weight: 700; color: var(--brand-ink-950); }
.who { display: flex; flex-direction: column; gap: 4px; }
.h { font-size: 13px; color: #4a5568; }
.seen { font-size: 12px; color: #4a5568; }
.fit { font-size: 20px; font-weight: 700; color: var(--brand-ocean-600); }
.of { font-size: 13px; color: #4a5568; font-weight: 500; }
.cell { border: 1px solid #ece7da; background: var(--brand-sand-50); border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.ctop { display: flex; justify-content: space-between; align-items: flex-start; gap: 6px; }
.lnk { flex: 0 0 auto; width: 32px; height: 32px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; border: 1px solid #cfc8b6; background: #fff; color: var(--brand-ocean-600); }
.lnk.on { background: var(--brand-ocean-600); border-color: var(--brand-ocean-600); color: #fff; }
.nosrc { font-size: 12px; color: #6b6555; }
.pop { display: flex; flex-direction: column; gap: 6px; background: #fff; border: 1px solid #e6e1d4; border-radius: 8px; padding: 8px 10px; }
.quote { font-size: 13px; font-style: italic; color: #33465e; }
.pop ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.pop li { display: flex; align-items: center; gap: 6px; font-size: 13px; min-width: 0; }
.pop a { font-weight: 600; white-space: nowrap; color: var(--brand-ocean-600); }
.pop .plain { font-weight: 600; white-space: nowrap; }
.pop .t { color: #4a5568; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.pop .ok { color: #0E7C80; font-weight: 700; }
@media (max-width: 600px) { .pct { padding: 16px; } }
</style>
